import {
  type CraftJobNetworkSnapshot,
  decodeWorkstationFuelState,
  getObjectFuelConfig,
  getRecipeById,
  getRecipesForWorkstation,
  getWorkstationTypeFromObject,
  type ObjectTypeValue,
  type RecipeDefinition,
  type WorkstationFuelNetworkSnapshot,
  type WorkstationTypeValue,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';
import { serverTick } from './network';

// ============================================================================
// Crafting State - Client-side crafting UI state management
// ============================================================================

// ============================================================================
// Signals
// ============================================================================

// Open workstation panel state
export interface OpenWorkstation {
  readonly networkId: number;
  readonly workstationType: WorkstationTypeValue;
  readonly tileX: number;
  readonly tileY: number;
}

export const openWorkstation = signal<OpenWorkstation | null>(null);

// Available recipes for current workstation
export const availableRecipeIds = signal<ReadonlyArray<string>>([]);

// Current craft queue for open workstation
export const craftQueue = signal<ReadonlyArray<CraftJobNetworkSnapshot>>([]);

// Fuel state for open workstation (if fuel-powered)
export const workstationFuel = signal<WorkstationFuelNetworkSnapshot | null>(null);

// Selected recipe
export const selectedRecipe = signal<RecipeDefinition | null>(null);

// Active recipe category tab
export const activeRecipeCategory = signal<string>('');

// Make X input state
export const makeXInputVisible = signal<boolean>(false);
export const makeXValue = signal<string>('');

// Global workstation queue counts (for all workstations, not just open one)
// Used to determine if "Work at" action should be visible
export const workstationQueueCounts = signal<Map<number, number>>(new Map());

export const getWorkstationQueueCount = ({ networkId }: { networkId: number }): number => {
  return workstationQueueCounts.value.get(networkId) ?? 0;
};

// ============================================================================
// Computed Values
// ============================================================================

// Get recipes for current workstation grouped by category
export const availableRecipes = computed<ReadonlyArray<RecipeDefinition>>(() => {
  const ws = openWorkstation.value;
  if (!ws) {
    return [];
  }
  return getRecipesForWorkstation({ workstationType: ws.workstationType });
});

// Get unique categories for current workstation
export const recipeCategories = computed<ReadonlyArray<string>>(() => {
  const recipes = availableRecipes.value;
  const categories = new Set<string>();
  for (const recipe of recipes) {
    categories.add(recipe.category);
  }
  return Array.from(categories).sort();
});

// Get recipes filtered by active category
export const filteredRecipes = computed<ReadonlyArray<RecipeDefinition>>(() => {
  const recipes = availableRecipes.value;
  const category = activeRecipeCategory.value;
  if (!category) {
    return recipes;
  }
  return recipes.filter((r) => r.category === category);
});

// Check if workstation panel is open
export const isWorkstationPanelOpen = computed<boolean>(() => {
  return openWorkstation.value !== null;
});

// ============================================================================
// Actions
// ============================================================================

export const openWorkstationPanel = ({
  networkId,
  objectType,
  tileX,
  tileY,
}: {
  networkId: number;
  objectType: ObjectTypeValue;
  tileX: number;
  tileY: number;
}): void => {
  const workstationType = getWorkstationTypeFromObject({ objectType });
  if (!workstationType) {
    console.warn(`Cannot open workstation panel: ${objectType} is not a workstation`);
    return;
  }

  const previousWorkstation = openWorkstation.value;
  const isReopeningSameWorkstation = previousWorkstation?.networkId === networkId;

  if (isReopeningSameWorkstation) {
    return;
  }

  openWorkstation.value = {
    networkId,
    workstationType,
    tileX,
    tileY,
  };

  // Reset state
  selectedRecipe.value = null;
  workstationFuel.value = null;
  activeRecipeCategory.value = '';
  makeXInputVisible.value = false;
  makeXValue.value = '';

  // Set first category as active
  const categories = recipeCategories.value;
  if (categories.length > 0) {
    activeRecipeCategory.value = categories[0] ?? '';
  }
};

export const closeWorkstationPanel = (): void => {
  openWorkstation.value = null;
  selectedRecipe.value = null;
  availableRecipeIds.value = [];
  craftQueue.value = [];
  workstationFuel.value = null;
  activeRecipeCategory.value = '';
  makeXInputVisible.value = false;
  makeXValue.value = '';
};

export const selectRecipe = ({ recipeId }: { recipeId: string }): void => {
  const recipe = getRecipeById({ recipeId });
  if (recipe) {
    selectedRecipe.value = recipe;
    makeXInputVisible.value = false;
    makeXValue.value = '';
  }
};

export const setActiveCategory = ({ category }: { category: string }): void => {
  activeRecipeCategory.value = category;
};

export const showMakeXInput = (): void => {
  makeXInputVisible.value = true;
  makeXValue.value = '';
};

export const hideMakeXInput = (): void => {
  makeXInputVisible.value = false;
  makeXValue.value = '';
};

export const setMakeXValue = ({ value }: { value: string }): void => {
  // Only allow numeric input
  const numericValue = value.replace(/[^0-9]/g, '');
  makeXValue.value = numericValue;
};

export const getMakeXQuantity = (): number => {
  const value = parseInt(makeXValue.value, 10);
  return Number.isNaN(value) || value < 1 ? 1 : value;
};

// ============================================================================
// Server Response Handlers
// ============================================================================

export const handleWorkstationData = ({
  workstationNetworkId,
  workstationType: _workstationType,
  queue,
  availableRecipeIds: recipeIds,
  fuel,
}: {
  workstationNetworkId: number;
  workstationType: WorkstationTypeValue;
  queue: ReadonlyArray<CraftJobNetworkSnapshot>;
  availableRecipeIds: ReadonlyArray<string>;
  fuel?: WorkstationFuelNetworkSnapshot;
}): void => {
  // Always update global queue count for this workstation
  const newCounts = new Map(workstationQueueCounts.value);
  newCounts.set(workstationNetworkId, queue.length);
  workstationQueueCounts.value = newCounts;

  // Only update UI state if this is for the currently open workstation
  const current = openWorkstation.value;
  if (!current || current.networkId !== workstationNetworkId) {
    return;
  }

  availableRecipeIds.value = recipeIds;
  craftQueue.value = queue;
  workstationFuel.value = fuel ?? null;
};

export const handleCraftOrderQueued = ({ job }: { job: CraftJobNetworkSnapshot }): void => {
  // Always update global queue count
  const newCounts = new Map(workstationQueueCounts.value);
  const current = newCounts.get(job.workstationNetworkId) ?? 0;
  newCounts.set(job.workstationNetworkId, current + 1);
  workstationQueueCounts.value = newCounts;

  // Add to queue if for open workstation
  const openWs = openWorkstation.value;
  if (!openWs || openWs.networkId !== job.workstationNetworkId) {
    return;
  }

  craftQueue.value = [...craftQueue.value, job];
};

export const handleCraftOrderUpdate = ({
  workstationNetworkId,
  orderId,
  progress,
  quantityCompleted,
  fuel,
}: {
  workstationNetworkId: number;
  orderId: number;
  progress: number;
  quantityCompleted: number;
  fuel?: WorkstationFuelNetworkSnapshot;
}): void => {
  const current = openWorkstation.value;
  if (!current || current.networkId !== workstationNetworkId) {
    return;
  }

  craftQueue.value = craftQueue.value.map((job) => {
    if (job.orderId === orderId) {
      return {
        ...job,
        progress,
        quantityCompleted,
      };
    }
    return job;
  });

  if (fuel) {
    workstationFuel.value = fuel;
  }
};

export const handleCraftOrderComplete = ({
  workstationNetworkId,
  orderId,
}: {
  workstationNetworkId: number;
  orderId: number;
}): void => {
  // Always update global queue count (decrement when order finishes)
  const newCounts = new Map(workstationQueueCounts.value);
  const current = newCounts.get(workstationNetworkId) ?? 0;
  newCounts.set(workstationNetworkId, Math.max(0, current - 1));
  workstationQueueCounts.value = newCounts;

  // Remove from UI queue if for open workstation
  const openWs = openWorkstation.value;
  if (!openWs || openWs.networkId !== workstationNetworkId) {
    return;
  }

  // Remove completed order from queue
  craftQueue.value = craftQueue.value.filter((job) => job.orderId !== orderId);
};

export const handleCraftOrderCancelled = ({ orderId }: { orderId: number }): void => {
  // Find the order to get its workstationNetworkId
  const job = craftQueue.value.find((j) => j.orderId === orderId);
  if (job) {
    // Decrement global queue count
    const newCounts = new Map(workstationQueueCounts.value);
    const current = newCounts.get(job.workstationNetworkId) ?? 0;
    newCounts.set(job.workstationNetworkId, Math.max(0, current - 1));
    workstationQueueCounts.value = newCounts;
  }

  // Remove cancelled order from queue
  craftQueue.value = craftQueue.value.filter((j) => j.orderId !== orderId);
};

const deriveFuelFromExistingSnapshot = ({
  worldObject,
  currentFuel,
}: {
  worldObject: WorldObjectSnapshot;
  currentFuel: WorkstationFuelNetworkSnapshot;
}): WorkstationFuelNetworkSnapshot => {
  const decoded = decodeWorkstationFuelState({
    state: worldObject.state,
    maxFuelUnits: currentFuel.maxFuelUnits,
  });
  return {
    ...currentFuel,
    fuelUnits: decoded.fuelUnits,
    burnRemainingMs: decoded.isBurning
      ? currentFuel.burnRemainingMs > 0
        ? currentFuel.burnRemainingMs
        : currentFuel.burnDurationMs
      : 0,
    burnStartedAtTick: decoded.isBurning
      ? currentFuel.burnStartedAtTick >= 0
        ? currentFuel.burnStartedAtTick
        : serverTick.value
      : -1,
  };
};

const deriveFuelFromObjectState = ({
  worldObject,
}: {
  worldObject: WorldObjectSnapshot;
}): WorkstationFuelNetworkSnapshot | null => {
  const fuelConfig = getObjectFuelConfig({ objectType: worldObject.objectType as ObjectTypeValue });
  if (!fuelConfig) {
    return null;
  }

  const decoded = decodeWorkstationFuelState({
    state: worldObject.state,
    maxFuelUnits: fuelConfig.maxFuelUnits,
  });
  return {
    fuelUnits: decoded.fuelUnits,
    maxFuelUnits: fuelConfig.maxFuelUnits,
    fuelPerCraft: fuelConfig.fuelUnitsPerCraft,
    burnDurationMs: fuelConfig.fuelUnitBurnDurationMs,
    burnRemainingMs: decoded.isBurning ? fuelConfig.fuelUnitBurnDurationMs : 0,
    burnStartedAtTick: decoded.isBurning ? serverTick.value : -1,
    acceptedFuelItems: fuelConfig.acceptedFuelItems.map((fuelItem) => ({
      itemType: fuelItem.itemType,
      fuelUnits: fuelItem.fuelUnits,
    })),
  };
};

export const handleWorkstationWorldObjectUpdate = ({
  worldObject,
  fuel,
}: {
  worldObject: WorldObjectSnapshot;
  fuel?: WorkstationFuelNetworkSnapshot;
}): void => {
  const currentWorkstation = openWorkstation.value;
  if (!currentWorkstation || currentWorkstation.networkId !== worldObject.networkId) {
    return;
  }

  if (fuel) {
    workstationFuel.value = fuel;
    return;
  }

  const currentFuel = workstationFuel.value;
  if (currentFuel) {
    workstationFuel.value = deriveFuelFromExistingSnapshot({ worldObject, currentFuel });
    return;
  }

  const derivedFuel = deriveFuelFromObjectState({ worldObject });
  if (derivedFuel) {
    workstationFuel.value = derivedFuel;
  }
};
